/**
 * Sistema de temas centralizado
 * Define colores y estilos para modo claro y oscuro
 *
 * NOTA: Este archivo usa los tokens del design-system.
 * Todos los valores deben venir de design-system/tokens/
 * NUNCA usar valores hardcodeados.
 */

import { themeColors } from '../design-system';

export type ThemeMode = 'light' | 'dark';

export interface Theme {
  name: string;
  mode: ThemeMode;
  colors: {
    // Fondos
    bg: {
      primary: string; // Fondo principal de contenido
      secondary: string; // Fondo secundario (cards, etc)
      surface: string; // Alias para secondary (compatibilidad)
      tertiary: string; // Fondo terciario (hover states)
      header: string; // Headers de paneles
      sidebar: string; // Sidebar y navegación
      input: string; // Inputs y campos de texto
      error: string; // Fondo para mensajes de error
      success: string; // Fondo para mensajes de éxito
    };
    // Textos
    text: {
      primary: string; // Texto principal
      secondary: string; // Texto secundario
      muted: string; // Texto deshabilitado/muted
      inverse: string; // Texto sobre fondos oscuros
      error: string; // Texto para mensajes de error
      success: string; // Texto para mensajes de éxito
    };
    // Bordes
    border: {
      primary: string; // Bordes principales
      secondary: string; // Bordes secundarios
      focus: string; // Bordes en foco
    };
    // Estados
    state: {
      hover: string; // Hover backgrounds
      active: string; // Active/selected states
      disabled: string; // Disabled backgrounds
    };
    // Acciones
    action: {
      primary: string; // Botones primarios
      primaryHover: string; // Hover de primarios
      secondary: string; // Botones secundarios
      danger: string; // Acciones destructivas
      dangerHover: string; // Hover de danger
    };
    // Navegación
    nav: {
      button: {
        inactive: string; // Botones de navegación inactivos
        inactiveHover: string; // Hover de botones inactivos
        active: string; // Botones de navegación activos (genérico)
        activeText: string; // Texto de botones activos
      };
    };
  };
}

export const lightTheme: Theme = {
  name: 'Light',
  mode: 'light',
  colors: {
    bg: {
      primary: 'bg-nb-bg', // Fondo principal (usa --nb-bg)
      secondary: 'bg-nb-surface', // Superficie (cards, paneles)
      surface: 'bg-nb-surface', // Alias para secondary (compatibilidad)
      tertiary: 'bg-nb-surface-hover', // Hover states
      header: 'bg-nb-surface', // Headers (mismo que secondary)
      sidebar: 'bg-nb-bg', // Sidebar (mismo que primary)
      input: 'bg-white', // Inputs (blanco puro)
      error: 'bg-red-50', // Fondo para mensajes de error
      success: 'bg-green-50', // Fondo para mensajes de éxito
    },
    text: {
      primary: 'text-nb-text', // Texto principal
      secondary: 'text-nb-text-secondary', // Texto secundario
      muted: 'text-nb-text-muted', // Texto muted (solid color, no opacity)
      inverse: 'text-white', // Texto sobre fondos oscuros
      error: 'text-red-600', // Texto para mensajes de error
      success: 'text-green-600', // Texto para mensajes de éxito
    },
    border: {
      primary: 'border-nb-border', // Bordes principales
      secondary: 'border-nb-border/50', // Bordes secundarios (más sutiles)
      focus: 'border-nb-accent', // Bordes en foco (accent)
    },
    state: {
      hover: 'hover:bg-nb-surface-hover', // Hover backgrounds
      active: 'bg-nb-surface-hover', // Active/selected states
      disabled: 'bg-nb-surface opacity-50', // Disabled backgrounds
    },
    action: {
      primary: 'bg-nb-accent', // Botones primarios
      primaryHover: 'hover:bg-nb-accent-hover', // Hover de primarios
      secondary: 'bg-nb-surface', // Botones secundarios
      danger: 'bg-nb-error', // Acciones destructivas
      dangerHover: 'hover:bg-nb-error/90', // Hover de danger
    },
    nav: {
      button: {
        inactive: 'bg-nb-surface', // Botones de navegación inactivos
        inactiveHover: 'hover:bg-nb-surface-hover', // Hover de botones inactivos
        active: 'bg-nb-surface-hover', // Botones de navegación activos (genérico)
        activeText: 'text-nb-text', // Texto de botones activos
      },
    },
  },
};

export const darkTheme: Theme = {
  name: 'Dark',
  mode: 'dark',
  colors: {
    bg: {
      primary: 'bg-nb-bg', // Fondo principal (usa --nb-bg del dark mode)
      secondary: 'bg-nb-surface', // Superficie (cards, paneles)
      surface: 'bg-nb-surface', // Alias para secondary (compatibilidad)
      tertiary: 'bg-nb-surface-hover', // Hover states
      header: 'bg-nb-surface', // Headers (mismo que secondary)
      sidebar: 'bg-nb-bg', // Sidebar (mismo que primary)
      input: 'bg-nb-surface', // Inputs (mismo que surface en dark)
      error: 'bg-red-900/20', // Fondo para mensajes de error (dark mode)
      success: 'bg-green-900/20', // Fondo para mensajes de éxito (dark mode)
    },
    text: {
      primary: 'text-nb-text', // Texto principal
      secondary: 'text-nb-text-secondary', // Texto secundario
      muted: 'text-nb-text-muted', // Texto muted (solid color, no opacity)
      inverse: 'text-nb-bg', // Texto sobre fondos oscuros (inverso)
      error: 'text-red-400', // Texto para mensajes de error (dark mode)
      success: 'text-green-400', // Texto para mensajes de éxito (dark mode)
    },
    border: {
      primary: 'border-nb-border', // Bordes principales
      secondary: 'border-nb-border/50', // Bordes secundarios (más sutiles)
      focus: 'border-nb-accent', // Bordes en foco (accent)
    },
    state: {
      hover: 'hover:bg-nb-surface-hover', // Hover backgrounds
      active: 'bg-nb-surface-hover', // Active/selected states
      disabled: 'bg-nb-surface opacity-50', // Disabled backgrounds
    },
    action: {
      primary: 'bg-nb-accent', // Botones primarios
      primaryHover: 'hover:bg-nb-accent-hover', // Hover de primarios
      secondary: 'bg-nb-surface-hover', // Botones secundarios
      danger: 'bg-nb-error', // Acciones destructivas
      dangerHover: 'hover:bg-nb-error/90', // Hover de danger
    },
    nav: {
      button: {
        inactive: 'bg-nb-surface', // Botones de navegación inactivos
        inactiveHover: 'hover:bg-nb-surface-hover', // Hover de botones inactivos
        active: 'bg-nb-surface-hover', // Botones de navegación activos (genérico)
        activeText: 'text-nb-text', // Texto de botones activos
      },
    },
  },
};

export const themes = {
  light: lightTheme,
  dark: darkTheme,
};
